import {param, typedQuery} from "../../typing";
import externalApi from "../../../api/external";
import {AccountMin, EngagementId, ThirdPartyId, TprmExternalQuestionnaireId} from "platformed-client/types";
import {HTTPError} from "platformed-client";

export const externalQuestionnaire = typedQuery(
  [
    "external",
    "portal",
    "externalQuestionnaire",
    param<AccountMin>("account"),
    param<ThirdPartyId>("thirdPartyId"),
    param<TprmExternalQuestionnaireId>("externalQuestionnaireId"),
  ],
  async (account, thirdPartyId, externalQuestionnaireId) => {
    return await externalApi.portal.getExternalQuestionnaire(account, thirdPartyId, externalQuestionnaireId);
  },
);

export const externalQuestionnaireAccessible = typedQuery(
  [
    "external",
    "portal",
    "externalQuestionnaireAccessible",
    param<AccountMin>("account"),
    param<ThirdPartyId>("thirdPartyId"),
    param<TprmExternalQuestionnaireId>("externalQuestionnaireId"),
  ],
  async (account, thirdPartyId, externalQuestionnaireId) => {
    try {
      await externalApi.portal.getExternalQuestionnaire(account, thirdPartyId, externalQuestionnaireId);
      return true;
    } catch (ex) {
      if (ex instanceof HTTPError && ex.response.status === 401) {
        return false;
      }
      throw ex;
    }
  },
);

export const template = typedQuery(
  [
    "external",
    "portal",
    "externalQuestionnaire",
    "template",
    param<AccountMin>("account"),
    param<ThirdPartyId>("thirdPartyId"),
    param<TprmExternalQuestionnaireId>("externalQuestionnaireId"),
  ],
  async (account, thirdPartyId, externalQuestionnaireId) => {
    return await externalApi.portal.getExternalQuestionnaireTemplate(account, thirdPartyId, externalQuestionnaireId);
  },
);

export const suggestions = typedQuery(
  [
    "external",
    "portal",
    "suggestions",
    param<AccountMin>("account"),
    param<ThirdPartyId>("thirdPartyId"),
    param<TprmExternalQuestionnaireId>("externalQuestionnaireId"),
  ],
  async (account, thirdPartyId, externalQuestionnaireId) => {
    return await externalApi.portal.listSuggestions(account, thirdPartyId, externalQuestionnaireId);
  },
);

export const suggestionDocuments = typedQuery(
  [
    "external",
    "portal",
    "suggestionDocuments",
    param<AccountMin>("account"),
    param<ThirdPartyId>("thirdPartyId"),
    param<TprmExternalQuestionnaireId>("externalQuestionnaireId"),
  ],
  async (account, thirdPartyId, externalQuestionnaireId) => {
    return await externalApi.portal.listSuggestionDocuments(account, thirdPartyId, externalQuestionnaireId);
  },
);

export const suggestionGenerationTasks = typedQuery(
  [
    "external",
    "portal",
    "suggestionGenerationTasks",
    param<AccountMin>("account"),
    param<ThirdPartyId>("thirdPartyId"),
    param<TprmExternalQuestionnaireId>("externalQuestionnaireId"),
  ],
  async (account, thirdPartyId, externalQuestionnaireId) => {
    return await externalApi.portal.listSuggestionGenerationTasks(account, thirdPartyId, externalQuestionnaireId);
  },
);

export const engagements = typedQuery(
  ["external", "portal", "engagements", param<AccountMin>("account"), param<ThirdPartyId>("thirdPartyId")],
  async (account, thirdPartyId) => {
    return await externalApi.portal.listEngagements(account, thirdPartyId);
  },
);

export const externalQuestionnaires = typedQuery(
  ["external", "portal", "externalQuestionnaires", param<AccountMin>("account"), param<EngagementId>("engagementId")],
  async (account, engagementId) => {
    return await externalApi.portal.listExternalQuestionnaires(account, engagementId);
  },
);

export const portalUsers = typedQuery(
  ["external", "portal", "portal", "portal_users", param<AccountMin>("account"), param<ThirdPartyId>("thirdPartyId")],
  async (account, thirdPartyId) => {
    return await externalApi.portal.listPortalUsers(account, thirdPartyId);
  },
);

export const portal = typedQuery(
  ["external", "portal", "third_party", param<AccountMin>("account"), param<ThirdPartyId>("thirdPartyId")],
  async (account, thirdPartyId) => {
    return await externalApi.portal.getPortal(account, thirdPartyId);
  },
);

export const portalReports = typedQuery(
  ["external", "portal", "reports", param<AccountMin>("account"), param<ThirdPartyId>("thirdPartyId")],
  async (account, thirdPartyId) => {
    return await externalApi.portal.listPortalReports(account, thirdPartyId);
  },
);

export const hasPermission = typedQuery(
  ["external", "portal", "has_permission", param<AccountMin>("account"), param<ThirdPartyId>("thirdPartyId")],
  async (account, thirdPartyId) => {
    try {
      const portal = await externalApi.portal.getPortal(account, thirdPartyId);
      return {hasPermission: true, portal};
    } catch (ex) {
      if (ex instanceof HTTPError && ex.response.status === 403) {
        return {hasPermission: false};
      }
      throw ex;
    }
  },
);
